import React from 'react';
import { TabsContent } from '@/components/ui/tabs';
import { TimeEntryRulesAccordion } from '@/components/settings/TimeEntryRulesAccordion';
import { TabAccessAlerts } from './TabAccessAlerts';
import type { SettingsFormState } from '../hooks/useSettingsFormState';

interface GeneralTabProps {
  form: SettingsFormState;
  canEditTimeEntryRules: boolean;
}

export const GeneralTab: React.FC<GeneralTabProps> = ({ form, canEditTimeEntryRules }) => {
  const {
    weekStartDay, workingProfile,
    showRole, requireRole, showCostCentre, requireCostCentre, showHoursToComplete, requireDescription, descriptionType,
    projectSelectionScope, showArchivedProjects, showProjectTimeRemaining,
    minimumEntryDuration, timeRounding, allowNegativeHours,
    historicalEntryLimit, allowFutureDatedEntries,
    enableCopyPreviousWeek, copyDescriptions, copyOnlyBillable, enableAutoSmartFill, enableAiInsights,
    tabsWithChanges,
    handleWeekStartDayChange, handleShowRoleChange, handleRequireRoleChange,
    handleShowCostCentreChange, handleRequireCostCentreChange, handleShowHoursToCompleteChange,
    handleRequireDescriptionChange, handleDescriptionTypeChange,
    handleProjectSelectionScopeChange, handleShowArchivedProjectsChange, handleShowProjectTimeRemainingChange,
    handleMinimumEntryDurationChange, handleTimeRoundingChange, handleAllowNegativeHoursChange,
    handleHistoricalEntryLimitChange, handleAllowFutureDatedEntriesChange,
    handleEnableCopyPreviousWeekChange, handleCopyDescriptionsChange, handleCopyOnlyBillableChange,
    handleEnableAutoSmartFillChange, handleEnableAiInsightsChange,
  } = form;

  return (
    <TabsContent value="general" className="space-y-6">
      <TabAccessAlerts
        canEdit={canEditTimeEntryRules}
        hasChanges={tabsWithChanges.has('general')}
        viewOnlyText="You have view-only access to Time Entry Rules. Contact your administrator to request edit access."
        unsavedText={'You have unsaved changes. Click "Save Settings" at the bottom to apply your changes.'}
      />

      <TimeEntryRulesAccordion
        weekStartDay={weekStartDay}
        workingProfile={workingProfile}
        showRole={showRole}
        requireRole={requireRole}
        showCostCentre={showCostCentre}
        requireCostCentre={requireCostCentre}
        projectSelectionScope={projectSelectionScope}
        showArchivedProjects={showArchivedProjects}
        showProjectTimeRemaining={showProjectTimeRemaining}
        minimumEntryDuration={minimumEntryDuration}
        timeRounding={timeRounding}
        allowNegativeHours={allowNegativeHours}
        historicalEntryLimit={historicalEntryLimit}
        allowFutureDatedEntries={allowFutureDatedEntries}
        enableCopyPreviousWeek={enableCopyPreviousWeek}
        copyDescriptions={copyDescriptions}
        copyOnlyBillable={copyOnlyBillable}
        enableAutoSmartFill={enableAutoSmartFill}
        enableAiInsights={enableAiInsights}
        onWeekStartDayChange={handleWeekStartDayChange}
        onShowRoleChange={handleShowRoleChange}
        onRequireRoleChange={handleRequireRoleChange}
        onShowCostCentreChange={handleShowCostCentreChange}
        onRequireCostCentreChange={handleRequireCostCentreChange}
        showHoursToComplete={showHoursToComplete}
        onShowHoursToCompleteChange={handleShowHoursToCompleteChange}
        requireDescription={requireDescription}
        onRequireDescriptionChange={handleRequireDescriptionChange}
        descriptionType={descriptionType}
        onDescriptionTypeChange={handleDescriptionTypeChange}
        onProjectSelectionScopeChange={handleProjectSelectionScopeChange}
        onShowArchivedProjectsChange={handleShowArchivedProjectsChange}
        onShowProjectTimeRemainingChange={handleShowProjectTimeRemainingChange}
        onMinimumEntryDurationChange={handleMinimumEntryDurationChange}
        onTimeRoundingChange={handleTimeRoundingChange}
        onAllowNegativeHoursChange={handleAllowNegativeHoursChange}
        onHistoricalEntryLimitChange={handleHistoricalEntryLimitChange}
        onAllowFutureDatedEntriesChange={handleAllowFutureDatedEntriesChange}
        onEnableCopyPreviousWeekChange={handleEnableCopyPreviousWeekChange}
        onCopyDescriptionsChange={handleCopyDescriptionsChange}
        onCopyOnlyBillableChange={handleCopyOnlyBillableChange}
        onEnableAutoSmartFillChange={handleEnableAutoSmartFillChange}
        onEnableAiInsightsChange={handleEnableAiInsightsChange}
      />

    </TabsContent>
  );
};
